// Invoice Query Hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';

// Get all invoices with filters
export function useInvoices(filters?: {
  startDate?: Date;
  endDate?: Date;
  clientId?: string;
  status?: string;
  type?: string;
}) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['invoices', company?.id, filters],
    queryFn: async () => {
      if (!company?.id) throw new Error('No company selected');

      let query = supabase
        .from('invoices')
        .select(`
          *,
          clients (
            id,
            name,
            email,
            phone
          ),
          invoice_items (
            id,
            product_id,
            service_id,
            quantity,
            unit_price,
            discount,
            discount_type,
            vat,
            total,
            description,
            unit,
            products (
              id,
              name,
              code,
              category,
              sale_price,
              purchase_price
            ),
            services (
              id,
              name,
              code,
              category,
              sale_price
            )
          )
        `)
        .eq('company_id', company.id);

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }
      if (filters?.clientId) {
        query = query.eq('client_id', filters.clientId);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.type) {
        query = query.eq('type', filters.type);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Invoice query error:', error);
        throw error;
      }
      return data || [];
    },
    enabled: !!company?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get single invoice
export function useInvoice(invoiceId: string) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      if (!company?.id) throw new Error('No company selected');

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          clients (
            id,
            name,
            email,
            phone,
            address,
            wilaya,
            tax_id,
            nrc,
            nis,
            article_number
          ),
          invoice_items (
            id,
            product_id,
            service_id,
            quantity,
            unit_price,
            discount,
            discount_type,
            vat,
            total,
            description,
            unit,
            products (
              id,
              name,
              code,
              category,
              sale_price,
              purchase_price
            ),
            services (
              id,
              name,
              code,
              category,
              sale_price
            )
          )
        `)
        .eq('id', invoiceId)
        .eq('company_id', company.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!invoiceId && !!company?.id,
  });
}

// Create invoice
export function useCreateInvoice() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async ({ items, ...invoiceData }: any) => {
      if (!company?.id) throw new Error('No company selected');

      // 0. Validate stock availability for PRODUCT items only (services have no stock)
      const productItems = items?.filter((item: any) => item.product_id) || [];
      for (const item of productItems) {
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('stock_quantity, name')
          .eq('id', item.product_id)
          .single();

        if (productError) throw productError;

        if (product.stock_quantity < item.quantity) {
          throw new Error(
            `Stock insuffisant pour "${product.name}". Stock disponible: ${product.stock_quantity}, Quantité demandée: ${item.quantity}`
          );
        }
      }

      // 1. Create invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          ...invoiceData,
          company_id: company.id,
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // 2. Create invoice items
      if (items && items.length > 0) {
        const invoiceItems = items.map((item: any) => ({
          invoice_id: invoice.id,
          product_id: item.product_id || null,
          service_id: item.service_id || null,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit || 'pcs',
          unit_price: item.unit_price,
          discount: item.discount || 0,
          discount_type: item.discount_type || 'percentage',
          vat: item.vat,
          total: item.total,
        }));

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(invoiceItems);

        if (itemsError) throw itemsError;

        // 3. Deduct stock for PRODUCT items only
        for (const item of productItems) {
          const { error: stockError } = await supabase.rpc('decrement_product_stock', {
            p_product_id: item.product_id,
            p_quantity: item.quantity,
          });

          if (stockError) {
            console.error('Stock deduction error:', stockError);
            throw new Error(`Erreur lors de la déduction du stock: ${stockError.message}`);
          }
        }
      }

      return invoice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock-products'] });
    },
  });
}

// Update invoice
export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, items, ...invoiceData }: any) => {
      if (!company?.id) throw new Error('No company selected');

      // 0. Get old items to restore stock
      const { data: oldInvoice } = await supabase
        .from('invoices')
        .select(`
          invoice_items (
            product_id,
            service_id,
            quantity
          )
        `)
        .eq('id', id)
        .single();

      // 1. Restore stock from old PRODUCT items only
      if (oldInvoice?.invoice_items) {
        for (const item of oldInvoice.invoice_items) {
          if (item.product_id) {
            await supabase.rpc('increment_product_stock', {
              p_product_id: item.product_id,
              p_quantity: item.quantity,
            });
          }
        }
      }

      // 2. Validate stock for new PRODUCT items only
      const productItems = items?.filter((item: any) => item.product_id) || [];
      for (const item of productItems) {
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('stock_quantity, name')
          .eq('id', item.product_id)
          .single();

        if (productError) throw productError;

        if (product.stock_quantity < item.quantity) {
          throw new Error(
            `Stock insuffisant pour "${product.name}". Stock disponible: ${product.stock_quantity}, Quantité demandée: ${item.quantity}`
          );
        }
      }

      // 3. Update invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .update(invoiceData)
        .eq('id', id)
        .eq('company_id', company.id)
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // 4. Delete old items
      await supabase
        .from('invoice_items')
        .delete()
        .eq('invoice_id', id);

      // 5. Insert new items
      if (items && items.length > 0) {
        const invoiceItems = items.map((item: any) => ({
          invoice_id: id,
          product_id: item.product_id || null,
          service_id: item.service_id || null,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit || 'pcs',
          unit_price: item.unit_price,
          discount: item.discount || 0,
          discount_type: item.discount_type || 'percentage',
          vat: item.vat,
          total: item.total,
        }));

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(invoiceItems);

        if (itemsError) throw itemsError;

        // 6. Deduct stock for PRODUCT items only
        for (const item of productItems) {
          const { error: stockError } = await supabase.rpc('decrement_product_stock', {
            p_product_id: item.product_id,
            p_quantity: item.quantity,
          });

          if (stockError) {
            console.error('Stock deduction error:', stockError);
            throw new Error(`Erreur lors de la déduction du stock: ${stockError.message}`);
          }
        }
      }

      return invoice;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock-products'] });
    },
  });
}

// Delete invoice
export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      // 1. Get invoice with items
      const { data: invoice, error: fetchError } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (
            id,
            product_id,
            service_id,
            quantity
          )
        `)
        .eq('id', invoiceId)
        .eq('company_id', company?.id)
        .single();

      if (fetchError) throw fetchError;

      // 2. Restore stock for PRODUCT items only
      if (invoice?.invoice_items) {
        for (const item of invoice.invoice_items) {
          if (item.product_id) {
            const { error: stockError } = await supabase.rpc('increment_product_stock', {
              p_product_id: item.product_id,
              p_quantity: item.quantity,
            });
            if (stockError) console.error('Stock restore error:', stockError);
          }
        }
      }

      // 3. Delete invoice (cascades to items)
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId)
        .eq('company_id', company?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// Get next invoice number
export function useNextInvoiceNumber(_companyId?: string, _type?: string) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['next-invoice-number', company?.id],
    queryFn: async () => {
      if (!company?.id) throw new Error('No company selected');

      // Get the latest invoice number
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "no rows returned" which is fine for first invoice
        throw error;
      }

      if (!data || !data.invoice_number) {
        // First invoice
        return `${new Date().getFullYear()}-001`;
      }

      // Extract number and increment
      const match = data.invoice_number.match(/(\d+)$/);
      if (match) {
        const num = parseInt(match[1]) + 1;
        const prefix = data.invoice_number.replace(/\d+$/, '');
        return `${prefix}${num.toString().padStart(3, '0')}`;
      }

      // Fallback
      return `${new Date().getFullYear()}-001`;
    },
    enabled: !!company?.id,
  });
}

// Mark invoice as paid
export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data, error } = await supabase
        .from('invoices')
        .update({ status: 'paid' })
        .eq('id', invoiceId)
        .eq('company_id', company?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
    },
  });
}

// Cancel invoice
export function useCancelInvoice() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      // 1. Get invoice items
      const { data: invoice, error: fetchError } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (
            id,
            product_id,
            service_id,
            quantity
          )
        `)
        .eq('id', invoiceId)
        .eq('company_id', company?.id)
        .single();

      if (fetchError) throw fetchError;

      // 2. Restore stock for PRODUCT items only
      if (invoice?.invoice_items) {
        for (const item of invoice.invoice_items) {
          if (item.product_id) {
            await supabase.rpc('increment_product_stock', {
              p_product_id: item.product_id,
              p_quantity: item.quantity,
            });
          }
        }
      }

      // 3. Update status
      const { data, error } = await supabase
        .from('invoices')
        .update({ status: 'cancelled' })
        .eq('id', invoiceId)
        .eq('company_id', company?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// Convert quote to invoice
export function useConvertQuoteToInvoice() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (quoteId: string) => {
      if (!company?.id) throw new Error('No company selected');

      // 1. Get the quote with all items
      const { data: quote, error: fetchError } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (
            product_id,
            service_id,
            description,
            quantity,
            unit,
            unit_price,
            discount,
            discount_type,
            vat,
            total
          )
        `)
        .eq('id', quoteId)
        .eq('company_id', company.id)
        .single();

      if (fetchError) throw fetchError;
      if (!quote) throw new Error('Quote not found');

      // 2. Get next invoice number
      const { data: lastInvoice } = await supabase
        .from('invoices')
        .select('invoice_number')
        .eq('company_id', company.id)
        .eq('type', 'invoice')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      let nextNumber = `${new Date().getFullYear()}-001`;
      if (lastInvoice?.invoice_number) {
        const match = lastInvoice.invoice_number.match(/(\d+)$/);
        if (match) {
          const num = parseInt(match[1]) + 1;
          const prefix = lastInvoice.invoice_number.replace(/\d+$/, '');
          nextNumber = `${prefix}${num.toString().padStart(3, '0')}`;
        }
      }

      // 3. Validate stock for product items
      const productItems = quote.invoice_items?.filter((item: any) => item.product_id) || [];
      for (const item of productItems) {
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('stock_quantity, name')
          .eq('id', item.product_id)
          .single();

        if (productError) throw productError;

        if (product.stock_quantity < item.quantity) {
          throw new Error(
            `Stock insuffisant pour "${product.name}". Stock disponible: ${product.stock_quantity}, Quantité demandée: ${item.quantity}`
          );
        }
      }

      // 4. Create invoice from quote data
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          company_id: company.id,
          type: 'invoice',
          invoice_number: nextNumber,
          client_id: quote.client_id,
          date: new Date().toISOString(),
          due_date: quote.due_date,
          status: 'draft',
          subtotal: quote.subtotal,
          vat_amount: quote.vat_amount,
          fiscal_stamp: quote.fiscal_stamp,
          discount: quote.discount,
          total: quote.total,
          paid: 0,
          balance: quote.total,
          notes: quote.notes,
          terms_and_conditions: quote.terms_and_conditions,
          language: quote.language || 'fr',
          sync_status: 'synced',
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // 5. Copy invoice items
      if (quote.invoice_items && quote.invoice_items.length > 0) {
        const newItems = quote.invoice_items.map((item: any) => ({
          invoice_id: invoice.id,
          product_id: item.product_id || null,
          service_id: item.service_id || null,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit || 'pcs',
          unit_price: item.unit_price,
          discount: item.discount || 0,
          discount_type: item.discount_type || 'percentage',
          vat: item.vat,
          total: item.total,
        }));

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(newItems);

        if (itemsError) throw itemsError;

        // 6. Deduct stock for product items
        for (const item of productItems) {
          const { error: stockError } = await supabase.rpc('decrement_product_stock', {
            p_product_id: item.product_id,
            p_quantity: item.quantity,
          });

          if (stockError) {
            console.error('Stock deduction error:', stockError);
          }
        }
      }

      // 7. Mark quote as converted (change status to "paid" to indicate it was fulfilled)
      await supabase
        .from('invoices')
        .update({ status: 'paid', notes: `${quote.notes || ''}\n[Converti en facture ${nextNumber}]`.trim() })
        .eq('id', quoteId);

      return invoice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock-products'] });
    },
  });
}
